import { ref } from 'vue';
import {
  getStorage,
  readStorageJSON,
  removeStorageItem,
  writeStorageJSON
} from '@/utils/secure-storage';

const ROUTE_STATE_STORAGE_KEY = 'h5:route-state';
const DEFAULT_TTL_MS = 30 * 60 * 1000;
const routeStateVersion = ref(0);

function readRouteStateMap() {
  return readStorageJSON(ROUTE_STATE_STORAGE_KEY, {
    entries: {},
    latest: {}
  }, {
    storage: getStorage('local')
  });
}

function writeRouteStateMap(value) {
  writeStorageJSON(ROUTE_STATE_STORAGE_KEY, value, {
    storage: getStorage('local')
  });
  routeStateVersion.value += 1;
}

function cleanupExpiredStates(store) {
  const now = Date.now();
  const nextStore = {
    entries: { ...(store?.entries || {}) },
    latest: { ...(store?.latest || {}) }
  };

  Object.entries(nextStore.entries).forEach(([key, entry]) => {
    if (!entry?.expireAt || entry.expireAt <= now) {
      delete nextStore.entries[key];
    }
  });

  Object.entries(nextStore.latest).forEach(([namespace, key]) => {
    if (!nextStore.entries[key] || nextStore.entries[key]?.namespace !== namespace) {
      delete nextStore.latest[namespace];
    }
  });

  return nextStore;
}

function generateStateKey(namespace) {
  return `${namespace}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeTarget(to) {
  if (typeof to === 'string') {
    return { path: to };
  }
  return { ...(to || {}) };
}

function isSameCurrentPath(target = {}) {
  if (typeof window === 'undefined') {
    return false;
  }
  const targetPath = String(target?.path || '').trim();
  if (!targetPath) {
    return false;
  }
  return window.location.pathname === targetPath;
}

export function getSavedRouteState(namespace, stateKey = '') {
  const safeNamespace = String(namespace || '').trim();
  if (!safeNamespace) {
    return {};
  }

  const store = cleanupExpiredStates(readRouteStateMap());
  writeRouteStateMap(store);

  const directEntry = stateKey ? store.entries[stateKey] : null;
  if (directEntry?.namespace === safeNamespace) {
    return directEntry.payload || {};
  }

  const latestKey = store.latest[safeNamespace];
  const latestEntry = latestKey ? store.entries[latestKey] : null;
  if (latestEntry?.namespace === safeNamespace) {
    return latestEntry.payload || {};
  }

  return {};
}

export function saveRouteState(namespace, payload = {}, options = {}) {
  const safeNamespace = String(namespace || '').trim();
  if (!safeNamespace) {
    return '';
  }

  const ttlMs = Math.max(1000, Number(options.ttlMs || DEFAULT_TTL_MS));
  const stateKey = generateStateKey(safeNamespace);
  const store = cleanupExpiredStates(readRouteStateMap());

  store.entries[stateKey] = {
    namespace: safeNamespace,
    payload: payload || {},
    expireAt: Date.now() + ttlMs
  };
  store.latest[safeNamespace] = stateKey;
  writeRouteStateMap(store);
  return stateKey;
}

export function getRouteState(route, namespace, fallback = {}) {
  const stateVersion = routeStateVersion.value;
  void stateVersion;
  const safeNamespace = String(namespace || '').trim();
  if (!safeNamespace) {
    return fallback;
  }
  const historyState = window.history.state || {};
  const payload = getSavedRouteState(safeNamespace, historyState?.__routeStateKey);
  if (Object.keys(payload || {}).length) {
    return payload;
  }

  return fallback;
}

export function clearRouteState(namespace = '') {
  const safeNamespace = String(namespace || '').trim();
  if (!safeNamespace) {
    removeStorageItem(ROUTE_STATE_STORAGE_KEY, {
      storage: getStorage('local')
    });
    routeStateVersion.value += 1;
    return;
  }

  const store = cleanupExpiredStates(readRouteStateMap());
  Object.entries(store.entries).forEach(([key, entry]) => {
    if (entry?.namespace === safeNamespace) {
      delete store.entries[key];
    }
  });
  delete store.latest[safeNamespace];
  writeRouteStateMap(store);
}

export function navigateWithState(router, to, options = {}) {
  const namespace = String(options.namespace || '').trim();
  const target = normalizeTarget(to);
  const nextState = { ...(target.state || {}) };

  if (namespace) {
    const stateKey = saveRouteState(namespace, options.payload || {}, {
      ttlMs: options.ttlMs
    });
    nextState.__routeStateKey = stateKey;
    nextState.__routeStateNamespace = namespace;
  }

  const nextTarget = {
    ...target,
    state: nextState
  };

  if (options.replace && isSameCurrentPath(target)) {
    window.history.replaceState({
      ...(window.history.state || {}),
      ...nextState
    }, '');
  }

  if (options.replace) {
    return router.replace(nextTarget);
  }
  return router.push(nextTarget);
}
